import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { ColumnGroup } from 'primereact/columngroup';
import { Row } from 'primereact/row';
import { InputText } from 'primereact/inputtext';
import { Tag } from 'primereact/tag';
import { Card } from 'primereact/card';
import { ProgressSpinner } from 'primereact/progressspinner';
import { TabView, TabPanel } from 'primereact/tabview';
import { Tooltip } from 'primereact/tooltip';
import moment from 'moment';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import {
    selectSalesData,
    selectSummary,
    selectSummaryCards,
    selectSummaryModeTooltips,
    selectPaymentModes,
    selectLoading,
    selectFromDate,
    selectToDate,
    selectSelectedBranch,
    selectError
} from '../../store/slices/salesReportsSlice';
import { getHourlySalesReport, getUserWiseSalesReport, formatDateForApi } from '../../utilities/api';
import useUser from '../../hooks/useUser';
import ReportSummaryWithChart from './ReportSummaryWithChart';
import { exportStyledExcel, buildReportFilterLines } from '../../utilities/excelExport';
import './billreports.css';

const SalesReportsContent = ({ isManagerView = false }) => {
    // Redux selectors
    const salesData = useSelector(selectSalesData);
    const summary = useSelector(selectSummary);
    const summaryCards = useSelector(selectSummaryCards);
    const summaryModeTooltips = useSelector(selectSummaryModeTooltips);
    const paymentModes = useSelector(selectPaymentModes);
    const loading = useSelector(selectLoading);
    const fromDate = useSelector(selectFromDate);
    const toDate = useSelector(selectToDate);
    const selectedBranch = useSelector(selectSelectedBranch);
    const error = useSelector(selectError);

    // Get role from localStorage
    const { userRole } = useUser();
    const rolename = userRole || localStorage.getItem('role') || '';
    const isCEO = rolename.toUpperCase() === 'CEO';
    const isAdmin = rolename.toUpperCase() === 'ADMIN';
    const canExport = !isManagerView && (isCEO || isAdmin);

    // Local state
    const [filteredSalesData, setFilteredSalesData] = useState([]);
    const [globalFilter, setGlobalFilter] = useState('');
    const [activeReportTab, setActiveReportTab] = useState(0);
    const [hourlyData, setHourlyData] = useState(null);
    const [userWiseData, setUserWiseData] = useState(null);
    const [loadingHourly, setLoadingHourly] = useState(false);
    const [loadingUserWise, setLoadingUserWise] = useState(false);
    const [expandedHourlyRows, setExpandedHourlyRows] = useState({});

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return '';
        return moment(dateString).isValid() ? moment(dateString).format('DD-MM-YYYY') : dateString;
    };

    // Format currency
    const formatCurrency = (amount) => {
        const num = parseFloat(amount || 0);
        return num.toLocaleString('en-IN', {
            maximumFractionDigits: 2,
            minimumFractionDigits: 2
        });
    };

    const isStrictNumericValue = (value) => /^-?\d+(\.\d+)?$/.test(String(value ?? '').trim());

    const isDateLikeField = (field) => {
        const fieldName = String(field || '').toLowerCase();
        return fieldName.includes('date') || fieldName.includes('time');
    };

    const shouldFormatAsCurrency = (field, value) => {
        if (value === null || value === undefined || value === '') return false;
        if (isDateLikeField(field)) return false;
        return isStrictNumericValue(value);
    };

    // Report payload (same as header: from_date, to_date, branch_id)
    const reportPayload = useCallback(() => {
        const branchId = selectedBranch?.branch_id || 'All';
        const payload = {
            from_date: fromDate ? formatDateForApi(fromDate) : '',
            to_date: toDate ? formatDateForApi(toDate) : '',
            branch_id: branchId
        };
        if (String(branchId).toLowerCase() === 'all') {
            const clientId = localStorage.getItem('clientid');
            if (clientId) payload.client_id = clientId;
        }
        return payload;
    }, [fromDate, toDate, selectedBranch]);

    // Reset hourly/user-wise cache when filters change
    useEffect(() => {
        setHourlyData(null);
        setUserWiseData(null);
    }, [fromDate, toDate, selectedBranch?.branch_id]);

    // Fetch hourly sales when Hourly tab is selected
    useEffect(() => {
        if (activeReportTab !== 1 || hourlyData !== null || !fromDate || !toDate) return;
        const fetchHourly = async () => {
            setLoadingHourly(true);
            try {
                const res = await getHourlySalesReport(reportPayload());
                if (res?.status === 'success' && res?.data) setHourlyData(res.data);
                else setHourlyData({});
            } catch {
                setHourlyData({});
            } finally {
                setLoadingHourly(false);
            }
        };
        fetchHourly();
    }, [activeReportTab, fromDate, toDate, hourlyData, reportPayload]);

    // Fetch user-wise sales when User-wise tab is selected
    useEffect(() => {
        if (activeReportTab !== 2 || userWiseData !== null || !fromDate || !toDate) return;
        const fetchUserWise = async () => {
            setLoadingUserWise(true);
            try {
                const res = await getUserWiseSalesReport(reportPayload());
                if (res?.status === 'success' && Array.isArray(res?.data)) setUserWiseData(res.data);
                else setUserWiseData([]);
            } catch {
                setUserWiseData([]);
            } finally {
                setLoadingUserWise(false);
            }
        };
        fetchUserWise();
    }, [activeReportTab, fromDate, toDate, userWiseData, reportPayload]);

    // Update filtered data when salesData or global search changes
    useEffect(() => {
        if (!globalFilter) {
            setFilteredSalesData(salesData);
            return;
        }
        const searchTerm = globalFilter.toLowerCase();
        const filtered = salesData.filter((item) => {
            return Object.keys(item).some((key) => {
                if (key === '_modeTooltips') return false;
                const value = item[key];
                if (value === null || value === undefined) return false;
                return String(value).toLowerCase().includes(searchTerm);
            });
        });
        setFilteredSalesData(filtered);
    }, [globalFilter, salesData]);

    // Format key as header (e.g. payment_date -> Payment Date, Total_CASH -> Total Cash)
    const formatKeyAsHeader = (key) => {
        if (!key) return '';
        return key.replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    };

    // Same as above but drops a leading "Total" (Total_CASH -> Cash) — the summary
    // tiles/table already group these under a payment-mode breakdown, so repeating
    // "Total" on every row is redundant and just eats the space the label needs.
    const formatSummaryLabel = (key) => {
        if (!key) return '';
        return key.replace(/^total_/i, '').replace(/_/g, ' ').replace(/\b\w/g, (l) => l.toUpperCase());
    };

    // Summary card style from key text (CASH, CARD, PAYTM, etc. are dynamic).
    // Colors are the validated categorical set (see dataviz palette) so payment
    // modes stay distinguishable for color-vision-deficient viewers, and the same
    // hex drives both the stat tile accent and its matching donut slice.
    const getSummaryCardStyle = (key) => {
        const k = (key || '').toUpperCase();
        // No hero-tile promotion here — everything renders as one ordered list
        // (payment modes, then Actual/NC/Expected/Cancelled/Total), matching the table.
        const isHighlight = false;
        const styles = {
            CASH: { bg: 'linear-gradient(135deg, #1baf7a 0%, #0f766e 100%)', icon: 'pi-wallet' },
            CARD: { bg: 'linear-gradient(135deg, #2a78d6 0%, #184f95 100%)', icon: 'pi-credit-card' },
            PAYTM: { bg: 'linear-gradient(135deg, #eda100 0%, #c98500 100%)', icon: 'pi-mobile' },
            UPI: { bg: 'linear-gradient(135deg, #eda100 0%, #c98500 100%)', icon: 'pi-mobile' },
            PHONEPE: { bg: 'linear-gradient(135deg, #eda100 0%, #c98500 100%)', icon: 'pi-mobile' },
            CREDIT: { bg: 'linear-gradient(135deg, #7c3aed 0%, #5b21b6 100%)', icon: 'pi-clock' },
            // Checked before NC — "CANCELLED" contains the substring "NC", so it
            // would otherwise be mis-styled as the NC (no-charge) category.
            CANCELLED: { bg: 'linear-gradient(135deg, #94a3b8 0%, #475569 100%)', icon: 'pi-ban' },
            NC: { bg: 'linear-gradient(135deg, #e34948 0%, #b91c1c 100%)', icon: 'pi-tag' },
            ACTUAL: { bg: 'linear-gradient(135deg, #2563eb 0%, #1e3a8a 100%)', icon: 'pi-chart-bar' },
            EXPECTED: { bg: 'linear-gradient(135deg, #0d9488 0%, #115e59 100%)', icon: 'pi-chart-line' },
            // Exact match on the grand-total key (Expected + Cancelled) — checked
            // last since "TOTAL_TOTAL" won't accidentally collide with the others.
            // Gold/amber so it stays visually distinct from Expected Sales' teal.
            TOTAL_TOTAL: { bg: 'linear-gradient(135deg, #ca8a04 0%, #854d0e 100%)', icon: 'pi-money-bill' }
        };
        const fallbackPalette = [
            { bg: 'linear-gradient(135deg, #0d9488 0%, #0f766e 100%)', icon: 'pi-wallet' },
            { bg: 'linear-gradient(135deg, #e87ba4 0%, #be3a72 100%)', icon: 'pi-mobile' },
            { bg: 'linear-gradient(135deg, #06b6d4 0%, #0891b2 100%)', icon: 'pi-dollar' }
        ];
        for (const [match, style] of Object.entries(styles)) {
            if (k.includes(match)) return { bg: style.bg, icon: style.icon, isHighlight };
        }
        const hash = (key || '').split('').reduce((acc, c) => acc + c.charCodeAt(0), 0);
        const fallback = fallbackPalette[Math.abs(hash) % fallbackPalette.length];
        return { bg: fallback.bg, icon: fallback.icon, isHighlight };
    };

    // Actual/Expected/Total are sums of the payment-mode rows (and of each other),
    // so they'd double-count if mixed into the donut's part-to-whole breakdown.
    const excludeSalesTotalFromChart = (key) => {
        const k = (key || '').toUpperCase();
        return k === 'TOTAL_ACTUAL_SALES' || k === 'TOTAL_EXPECTED_SALES' || k === 'TOTAL_TOTAL';
    };

    // Actual Sales, Expected Sales and the grand Total headings get bold/colored
    // emphasis text in the summary (Actual/Expected keep their own accent color;
    // Total keeps its gold accent — see styles above).
    const SALES_EMPHASIZE_KEYS = ['TOTAL_ACTUAL_SALES', 'TOTAL_EXPECTED_SALES', 'TOTAL_TOTAL'];
    const emphasizeSalesTotal = (key) => SALES_EMPHASIZE_KEYS.includes((key || '').toUpperCase());

    // These 5 are fixed table columns (Actual Sales, NC, Expected Sales, Cancelled,
    // Total) — always show them in the summary even if the aggregate is 0 for the
    // selected range, unlike an optional payment mode (Paytm, etc.) that's fine to hide.
    const SALES_ALWAYS_SHOW_KEYS = ['TOTAL_ACTUAL_SALES', 'TOTAL_NC', 'TOTAL_EXPECTED_SALES', 'TOTAL_CANCELLED', 'TOTAL_TOTAL'];
    const alwaysShowSalesField = (key) => SALES_ALWAYS_SHOW_KEYS.includes((key || '').toUpperCase());

    // Same NC/Credit/Cancelled hover breakdown as the table's mode cells, but
    // aggregated over the whole selected range (only payment-mode rows get one).
    const getSalesSummaryTooltip = (key) => {
        const mode = paymentModes.find((m) => `Total_${m}`.toUpperCase() === (key || '').toUpperCase());
        if (!mode) return null;
        const t = summaryModeTooltips?.[mode] || {};
        return [`NC: ₹${formatCurrency(t.NC)}`, `Credit: ₹${formatCurrency(t.Credit)}`, `Cancelled: ₹${formatCurrency(t.Cancelled)}`].join('\n');
    };

    // A payment mode with 0 actual sales across every date in the current range/branch
    // is unused for this report — hide its column from the table and exports, same as
    // the summary already hides an all-zero breakdown row.
    const usedPaymentModes = paymentModes.filter((mode) => salesData.some((row) => Number(row[mode]) > 0));
    const unusedPaymentModes = paymentModes.filter((mode) => !usedPaymentModes.includes(mode));

    // Export to PDF
    const exportToPDF = () => {
        if (filteredSalesData.length === 0) {
            return;
        }

        const doc = new jsPDF();
        doc.setFontSize(16);
        doc.text('Sales Report', 14, 15);

        // Add filter info
        doc.setFontSize(10);
        let yPos = 25;
        if (fromDate && toDate) {
            doc.text(`Period: ${moment(fromDate).format('DD-MM-YYYY')} to ${moment(toDate).format('DD-MM-YYYY')}`, 14, yPos);
            yPos += 5;
        }
        if (selectedBranch) {
            doc.text(`Branch: ${selectedBranch.branch_name || selectedBranch}`, 14, yPos);
            yPos += 5;
        }

        const keys = filteredSalesData.length > 0 ? Object.keys(filteredSalesData[0]).filter((k) => k !== '_modeTooltips' && !unusedPaymentModes.includes(k)) : [];
        const headers = keys.map((k) => formatKeyAsHeader(k));

        const tableBody = filteredSalesData.map((item) =>
            keys.map((key) => {
                const value = item[key];
                if (value === null || value === undefined || value === '') return '';
                const valueStr = String(value).trim();
                if (shouldFormatAsCurrency(key, valueStr)) return `₹${formatCurrency(Number(valueStr))}`;
                return valueStr;
            })
        );

        // Add summary row: map table keys to summary keys (e.g. Cash -> Total_Actual_Cash)
        if (Object.keys(summary).length > 0 && keys.length > 0) {
            const summaryRow = keys.map((key, idx) => {
                if (idx === 0) return 'TOTAL';
                const val = summary[`Total_${key}`];
                if (val !== undefined && val !== null) {
                    const num = parseFloat(val);
                    return isNaN(num) ? String(val) : `₹${formatCurrency(num)}`;
                }
                return '';
            });
            tableBody.push(summaryRow);
        }

        autoTable(doc, {
            startY: yPos + 5,
            head: [headers],
            body: tableBody,
            styles: { fontSize: 8 },
            headStyles: { fillColor: [0, 122, 255] },
            footStyles: { fontStyle: 'bold', fillColor: [240, 240, 240] },
            didParseCell: function (data) {
                // Make summary row bold
                if (data.row.index === tableBody.length - 1 && Object.keys(summary).length > 0) {
                    data.cell.styles.fontStyle = 'bold';
                    data.cell.styles.fillColor = [240, 240, 240];
                }
            }
        });

        doc.save(`SalesReport_${moment().format('YYYY-MM-DD')}.pdf`);
    };

    // Export to Excel
    const exportToExcel = async () => {
        if (filteredSalesData.length === 0) {
            return;
        }

        const keys = Object.keys(filteredSalesData[0]).filter((k) => k !== '_modeTooltips' && !unusedPaymentModes.includes(k));
        const columns = keys.map((key) => ({
            header: formatKeyAsHeader(key),
            getValue: (item) => {
                const value = item[key];
                if (value === null || value === undefined || value === '') return '-';
                return shouldFormatAsCurrency(key, filteredSalesData[0][key]) ? Number(String(value).trim()) : value;
            },
            numeric: shouldFormatAsCurrency(key, filteredSalesData[0][key])
        }));

        await exportStyledExcel({
            fileName: `SalesReport_${moment().format('YYYY-MM-DD')}.xlsx`,
            sheetName: 'Sales Report',
            title: 'Sales Report',
            filterLines: buildReportFilterLines({ fromDate, toDate, selectedBranch }),
            columns,
            rows: filteredSalesData
        });
    };

    // Dynamic cell body: show value as date or currency based on content
    const cellBodyTemplate = (field) => (rowData) => {
        const value = rowData[field];
        if (value === null || value === undefined || value === '') return '-';
        if (shouldFormatAsCurrency(field, value)) {
            return `₹${formatCurrency(Number(String(value).trim()))}`;
        }
        return String(value);
    };

    // Payment-mode cell (Cash/Card/Paytm/...): shows the actual-sales amount for
    // that mode, with a hover tooltip revealing the NC/Credit/Cancelled amounts
    // rung up in the same mode on that date (those aren't shown as their own columns).
    const modeCellBodyTemplate = (mode) => (rowData) => {
        const value = rowData[mode];
        const t = rowData._modeTooltips?.[mode] || {};
        const tooltip = [`NC: ₹${formatCurrency(t.NC)}`, `Credit: ₹${formatCurrency(t.Credit)}`, `Cancelled: ₹${formatCurrency(t.Cancelled)}`].join('\n');
        return (
            <div className="payment-mode-cell" data-pr-tooltip={tooltip} data-pr-position="top">
                ₹{formatCurrency(value)}
            </div>
        );
    };

    // Fixed column layout: Date, one column per discovered payment mode (Cash/Card/
    // Paytm/...) carrying Actual Sales, then Actual Sales / NC / Expected Sales /
    // Cancelled / Total. Credit is intentionally not its own column (see mode tooltip above).
    const getColumns = () => {
        const source = filteredSalesData.length > 0 ? filteredSalesData : salesData;
        if (source.length === 0) return [];

        const dateColumn = {
            field: 'payment_date',
            header: 'Payment Date',
            sortable: true,
            body: cellBodyTemplate('payment_date'),
            style: { minWidth: '120px', textAlign: 'left' }
        };

        const modeColumns = usedPaymentModes.map((mode) => ({
            field: mode,
            header: mode,
            sortable: true,
            body: modeCellBodyTemplate(mode),
            style: { minWidth: '120px', textAlign: 'right' }
        }));

        const totalColumns = [
            { field: 'Actual_Sales', header: 'Actual Sales' },
            { field: 'NC', header: 'NC' },
            { field: 'Expected_Sales', header: 'Expected Sales' },
            { field: 'Cancelled', header: 'Cancelled' },
            { field: 'Total', header: 'Total' }
        ].map((col) => ({
            ...col,
            sortable: true,
            body: cellBodyTemplate(col.field),
            style: { minWidth: '130px', textAlign: 'right' }
        }));

        return [dateColumn, ...modeColumns, ...totalColumns];
    };

    const columns = getColumns();

    // Hourly Sales (pivoted):
    // API format: { "DD-MM-YYYY": [ { time_range, Total_Hourly_Sales, payments: { CASH: 100, PAYTM: 50, ... } }, ... ], ... }
    // UI required:
    // - Column header: time_range
    // - Row header: payment modes (CASH, PAYTM, etc.)
    // - Row expand (accordion): date-wise sales for that payment mode
    const hourlyDates = hourlyData && typeof hourlyData === 'object' && !Array.isArray(hourlyData) ? Object.keys(hourlyData) : [];

    const hourlyTimeRanges = (() => {
        if (!hourlyDates.length) return [];
        const set = new Set();
        for (const d of hourlyDates) {
            const slots = Array.isArray(hourlyData?.[d]) ? hourlyData[d] : [];
            for (const s of slots) set.add(s?.time_range || '-');
        }
        const parseStartMinutes = (tr) => {
            const m = String(tr || '').match(/(\d{1,2})(?::(\d{2}))?\s*(AM|PM)?/i);
            if (!m) return Number.POSITIVE_INFINITY;
            let hh = parseInt(m[1], 10);
            const mm = parseInt(m[2] || '0', 10);
            const ap = (m[3] || '').toUpperCase();
            if (ap === 'PM' && hh < 12) hh += 12;
            if (ap === 'AM' && hh === 12) hh = 0;
            return hh * 60 + mm;
        };
        return Array.from(set).sort((a, b) => parseStartMinutes(a) - parseStartMinutes(b));
    })();

    const hourlyPaymentModes = (() => {
        if (!hourlyDates.length) return [];
        const set = new Set();
        for (const d of hourlyDates) {
            const slots = Array.isArray(hourlyData?.[d]) ? hourlyData[d] : [];
            for (const s of slots) {
                const p = s?.payments && typeof s.payments === 'object' && !Array.isArray(s.payments) ? s.payments : {};
                Object.keys(p).forEach((k) => set.add(k));
            }
        }
        return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)));
    })();

    const toNum = (v) => {
        const n = parseFloat(v ?? 0);
        return Number.isFinite(n) ? n : 0;
    };

    const hourlyPivotRows = (() => {
        if (!hourlyDates.length || !hourlyPaymentModes.length || !hourlyTimeRanges.length) return [];

        // mode -> time_range -> total
        const modeTotals = new Map();
        // mode -> date -> (time_range -> total)
        const modeByDate = new Map();

        for (const mode of hourlyPaymentModes) {
            modeTotals.set(mode, new Map(hourlyTimeRanges.map((tr) => [tr, 0])));
            modeByDate.set(mode, new Map(hourlyDates.map((d) => [d, new Map(hourlyTimeRanges.map((tr) => [tr, 0]))])));
        }

        for (const d of hourlyDates) {
            const slots = Array.isArray(hourlyData?.[d]) ? hourlyData[d] : [];
            for (const slot of slots) {
                const tr = slot?.time_range || '-';
                const payments = slot?.payments && typeof slot.payments === 'object' && !Array.isArray(slot.payments) ? slot.payments : {};
                for (const mode of hourlyPaymentModes) {
                    if (payments?.[mode] === undefined) continue;
                    const amt = toNum(payments[mode]);
                    const tMap = modeTotals.get(mode);
                    const dMap = modeByDate.get(mode);
                    if (tMap?.has(tr)) tMap.set(tr, (tMap.get(tr) || 0) + amt);
                    const perDate = dMap?.get(d);
                    if (perDate?.has(tr)) perDate.set(tr, (perDate.get(tr) || 0) + amt);
                }
            }
        }

        return hourlyPaymentModes.map((mode) => {
            const row = { payment_mode: mode };
            const tMap = modeTotals.get(mode) || new Map();
            let total = 0;
            hourlyTimeRanges.forEach((tr) => {
                const v = toNum(tMap.get(tr));
                row[tr] = v;
                total += v;
            });
            row.total = total;

            const dMap = modeByDate.get(mode) || new Map();
            const byDateRows = hourlyDates.map((d) => {
                const r = { date: d };
                const perDate = dMap.get(d) || new Map();
                let t = 0;
                hourlyTimeRanges.forEach((tr) => {
                    const v = toNum(perDate.get(tr));
                    r[tr] = v;
                    t += v;
                });
                r.total = t;
                return r;
            });
            row._byDate = byDateRows;
            return row;
        });
    })();

    // User-wise data: API returns a flat array of
    // { payment_date, user_id, username, total_sales, bill_count, payment_modes: { CASH: 100, CARD: 50, ... } }
    const userWisePaymentModes = (() => {
        if (!Array.isArray(userWiseData)) return [];
        const set = new Set();
        for (const r of userWiseData) {
            const modes = r?.payment_modes && typeof r.payment_modes === 'object' && !Array.isArray(r.payment_modes) ? r.payment_modes : {};
            Object.keys(modes).forEach((k) => set.add(k));
        }
        return Array.from(set).sort((a, b) => String(a).localeCompare(String(b)));
    })();

    const userWiseRows = Array.isArray(userWiseData)
        ? userWiseData.map((r, i) => {
              const modes = r?.payment_modes && typeof r.payment_modes === 'object' && !Array.isArray(r.payment_modes) ? r.payment_modes : {};
              const row = {
                  id: `${r.payment_date}-${r.user_id}-${i}`,
                  date: r.payment_date,
                  user_id: r.user_id,
                  username: r.username,
                  total_sales: r.total_sales ?? '0',
                  bill_count: r.bill_count ?? 0
              };
              userWisePaymentModes.forEach((mode) => {
                  row[`mode_${mode}`] = modes?.[mode] ?? 0;
              });
              return row;
          })
        : [];

    if (loading) {
        return (
            <div className="bill-reports-container">
                <Card>
                    <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                        <ProgressSpinner />
                    </div>
                </Card>
            </div>
        );
    }

    return (
        <div className="bill-reports-container">
            {error && (
                <div
                    className="mb-3 p-3 border-round"
                    style={{
                        backgroundColor: '#fef2f2',
                        border: '1px solid #fecaca',
                        color: '#b91c1c'
                    }}
                >
                    <span>{error}</span>
                </div>
            )}
            {!isManagerView && Object.keys(summaryCards).length > 0 && (
                <ReportSummaryWithChart
                    summary={summaryCards}
                    fromDate={fromDate}
                    toDate={toDate}
                    selectedBranch={selectedBranch}
                    title="Sales Summary"
                    subtitle="Overview of sales by payment type"
                    getCardStyle={getSummaryCardStyle}
                    formatLabel={formatSummaryLabel}
                    isCurrency={() => true}
                    excludeFromChart={excludeSalesTotalFromChart}
                    emphasizeKey={emphasizeSalesTotal}
                    alwaysShowKey={alwaysShowSalesField}
                    getTooltip={getSalesSummaryTooltip}
                />
            )}

            {/* Data Table Section - Tabs */}
            <Card>
                <div className="flex flex-wrap justify-content-between align-items-center mb-3 gap-3">
                    <h3 className="text-xl font-semibold m-0">Report Data</h3>
                </div>
                <div className="report-data-tabs">
                    <TabView activeIndex={activeReportTab} onTabChange={(e) => setActiveReportTab(e.index)}>
                        <TabPanel header="Sales by date range">
                            <div className="flex flex-wrap justify-content-between align-items-center mb-4 gap-3">
                                <div className="flex align-items-center gap-2">{filteredSalesData.length > 0 && <Tag value={`${filteredSalesData.length} Records`} severity="info" />}</div>
                                <div className="flex flex-wrap gap-2">
                                    <span className="p-input-icon-left w-full sm:w-auto" style={{ minWidth: '250px' }}>
                                        <i className="pi pi-search"></i>
                                        <InputText value={globalFilter} onChange={(e) => setGlobalFilter(e.target.value)} placeholder="Search sales..." className="w-full" />
                                    </span>
                                    {canExport && (
                                        <>
                                            <Button label="Export Excel" icon="pi pi-file-excel" className="p-button-success" onClick={exportToExcel} disabled={filteredSalesData.length === 0} />
                                            <Button label="Export PDF" icon="pi pi-file-pdf" className="p-button-danger" onClick={exportToPDF} disabled={filteredSalesData.length === 0} />
                                        </>
                                    )}
                                </div>
                            </div>
                            {loading ? (
                                <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                                    <ProgressSpinner />
                                </div>
                            ) : filteredSalesData.length === 0 ? (
                                <div className="flex flex-column align-items-center justify-content-center p-6">
                                    <i className="pi pi-inbox text-6xl text-color-secondary mb-3"></i>
                                    <p className="text-xl text-color-secondary">No data available</p>
                                    <p className="text-color-secondary mt-2">
                                        {salesData.length === 0 ? (fromDate && toDate ? 'No sales data found for the selected filters' : 'Please apply filters to view sales reports') : 'No records match your search criteria'}
                                    </p>
                                </div>
                            ) : (
                                <>
                                <Tooltip key={`sales-modes-${filteredSalesData.length}`} target=".payment-mode-cell" />
                                <DataTable
                                    value={filteredSalesData}
                                    showGridlines
                                    paginator
                                    rows={25}
                                    stripedRows
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    paginatorTemplate="RowsPerPageDropdown FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink"
                                    currentPageReportTemplate="{first} to {last} of {totalRecords} records"
                                    tableStyle={{ minWidth: '50rem' }}
                                    className="p-datatable-sm"
                                    emptyMessage="No sales data found"
                                    footerColumnGroup={
                                        !isManagerView && Object.keys(summary).length > 0 && columns.length > 0 ? (
                                            <ColumnGroup>
                                                <Row>
                                                    {columns.map((col, index) => {
                                                        const footerContent = index === 0 ? 'Grand Total:' : `₹${formatCurrency(summary[`Total_${col.field}`] ?? 0)}`;
                                                        return <Column key={col.field} footer={footerContent} style={{ textAlign: index === 0 ? 'left' : 'right', fontWeight: 'bold', fontSize: '1rem' }} />;
                                                    })}
                                                </Row>
                                            </ColumnGroup>
                                        ) : null
                                    }
                                >
                                    {columns.map((col) => (
                                        <Column key={col.field} field={col.field} header={col.header} sortable={col.sortable} body={col.body} style={col.style} />
                                    ))}
                                </DataTable>
                                </>
                            )}
                        </TabPanel>
                        <TabPanel header="Hourly sales">
                            {loadingHourly ? (
                                <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                                    <ProgressSpinner />
                                </div>
                            ) : hourlyPivotRows.length === 0 ? (
                                <div className="flex flex-column align-items-center justify-content-center p-6">
                                    <i className="pi pi-clock text-6xl text-color-secondary mb-3"></i>
                                    <p className="text-xl text-color-secondary">No hourly data</p>
                                    <p className="text-color-secondary mt-2">Data loads when you open this tab with date range and branch selected.</p>
                                </div>
                            ) : (
                                <DataTable
                                    value={hourlyPivotRows}
                                    dataKey="payment_mode"
                                    showGridlines
                                    paginator
                                    rows={25}
                                    stripedRows
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    tableStyle={{ minWidth: '60rem' }}
                                    className="p-datatable-sm"
                                    emptyMessage="No hourly sales"
                                    expandedRows={expandedHourlyRows}
                                    onRowToggle={(e) => setExpandedHourlyRows(e.data)}
                                    rowExpansionTemplate={(row) => (
                                        <div className="p-3">
                                            <strong className="block mb-2">Date wise sales</strong>
                                            <DataTable value={row._byDate || []} showGridlines className="p-datatable-sm" tableStyle={{ minWidth: '50rem' }} emptyMessage="No date-wise data">
                                                <Column field="date" header="Date" sortable style={{ minWidth: '110px' }} />
                                                {hourlyTimeRanges.map((tr) => (
                                                    <Column key={tr} field={tr} header={tr} body={(r) => `₹${formatCurrency(r[tr])}`} style={{ textAlign: 'right', minWidth: '120px' }} />
                                                ))}
                                                <Column field="total" header="Total" body={(r) => `₹${formatCurrency(r.total)}`} style={{ textAlign: 'right', minWidth: '120px', fontWeight: 600 }} />
                                            </DataTable>
                                        </div>
                                    )}
                                >
                                    <Column expander style={{ width: '3rem' }} />
                                    <Column field="payment_mode" header="Payment Mode" sortable style={{ minWidth: '140px' }} />
                                    {hourlyTimeRanges.map((tr) => (
                                        <Column key={tr} field={tr} header={tr} body={(row) => `₹${formatCurrency(row[tr])}`} style={{ textAlign: 'right', minWidth: '120px' }} />
                                    ))}
                                    <Column field="total" header="Total" sortable body={(row) => `₹${formatCurrency(row.total)}`} style={{ textAlign: 'right', minWidth: '140px', fontWeight: 700 }} />
                                </DataTable>
                            )}
                        </TabPanel>
                        <TabPanel header="User wise sales">
                            {loadingUserWise ? (
                                <div className="flex justify-content-center align-items-center" style={{ minHeight: '300px' }}>
                                    <ProgressSpinner />
                                </div>
                            ) : userWiseRows.length === 0 ? (
                                <div className="flex flex-column align-items-center justify-content-center p-6">
                                    <i className="pi pi-users text-6xl text-color-secondary mb-3"></i>
                                    <p className="text-xl text-color-secondary">No user-wise data</p>
                                    <p className="text-color-secondary mt-2">Data loads when you open this tab with date range and branch selected.</p>
                                </div>
                            ) : (
                                <DataTable
                                    value={userWiseRows}
                                    dataKey="id"
                                    showGridlines
                                    paginator
                                    rows={25}
                                    stripedRows
                                    rowsPerPageOptions={[10, 25, 50, 100]}
                                    tableStyle={{ minWidth: '50rem' }}
                                    className="p-datatable-sm"
                                    emptyMessage="No user-wise sales"
                                >
                                    <Column field="date" header="Date" sortable style={{ minWidth: '110px' }} />
                                    <Column field="username" header="User" sortable style={{ minWidth: '140px' }} />
                                    <Column field="bill_count" header="Bill Count" sortable style={{ textAlign: 'right', minWidth: '110px' }} />
                                    {userWisePaymentModes.map((mode) => (
                                        <Column
                                            key={mode}
                                            field={`mode_${mode}`}
                                            header={mode}
                                            sortable
                                            body={(row) => `₹${formatCurrency(row[`mode_${mode}`])}`}
                                            style={{ textAlign: 'right', minWidth: '110px' }}
                                        />
                                    ))}
                                    <Column field="total_sales" header="Total Sales" sortable body={(row) => `₹${formatCurrency(row.total_sales)}`} style={{ textAlign: 'right', minWidth: '130px', fontWeight: 600 }} />
                                </DataTable>
                            )}
                        </TabPanel>
                    </TabView>
                </div>
            </Card>
        </div>
    );
};

export default SalesReportsContent;
